import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, TextField, Button, Paper, Grid, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { REMOTE_OPTIONS } from './constants';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';

/**
 * LocationSelectionReadOnly component - displays selected locations in read-only mode for chat history
 * @param {Object} props - Component props
 * @param {Array} props.selectedLocations - Array of selected location strings
 * @returns {JSX.Element} The rendered read-only component
 */
export const LocationSelectionReadOnly = ({
  selectedLocations = []
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        borderRadius: '12px',
        border: '1px solid rgba(192, 197, 205, 0.5)',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        marginBottom: '12px'
      }}
    >
      {/* Content */}
      <Box
        sx={{
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        <Typography 
          sx={{ 
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '1.375em',
            color: '#101828'
          }}
        >
          Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍
          {'\n'}Pick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨
        </Typography>

        {/* Common Locations Section */}
        <Box>
          <Typography 
            sx={{ 
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              color: '#101828',
              marginBottom: '8px'
            }}
          >
            Common Locations
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {selectedLocations
              .filter(loc => !REMOTE_OPTIONS.some(option => option.label === loc))
              .map((location, index) => (
                <Chip
                  key={index}
                  icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#0F42BC !important' }} />}
                  label={location}
                  sx={{
                    backgroundColor: '#E1EFFF',
                    color: '#0F42BC',
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '1.4285714285714286em',
                    borderRadius: '16px',
                    height: 'auto',
                    padding: '4px 12px 4px 10px',
                    '& .MuiChip-label': {
                      padding: '0 4px 0 0'
                    },
                    '& .MuiChip-icon': {
                      margin: '0 4px 0 0',
                      color: '#0F42BC !important'
                    }
                  }}
                />
              ))}
          </Box>
        </Box>

        {/* Remote Options Section */}
        {selectedLocations.some(loc => REMOTE_OPTIONS.some(option => option.label === loc)) && (
          <Box>
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '1.4285714285714286em',
                color: '#101828',
                marginBottom: '8px'
              }}
            >
              or select a remote option
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {selectedLocations
                .filter(loc => REMOTE_OPTIONS.some(option => option.label === loc))
                .map((location, index) => (
                  <Chip
                    key={index}
                    icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#0F42BC !important' }} />}
                    label={location}
                    sx={{
                      backgroundColor: '#E1EFFF',
                      color: '#0F42BC',
                      fontFamily: 'Roboto',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '1.4285714285714286em',
                      borderRadius: '16px',
                      height: 'auto',
                      padding: '4px 12px 4px 10px',
                      '& .MuiChip-label': {
                        padding: '0 4px 0 0'
                      },
                      '& .MuiChip-icon': {
                        margin: '0 4px 0 0',
                        color: '#0F42BC !important'
                      }
                    }}
                  />
                ))}
            </Box>
          </Box>
        )}

        {/* Confirmed Label */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 12px',
            borderRadius: '8px',
            bgcolor: '#F9FAFB',
            border: '1px solid #EAECF0',
            alignSelf: 'flex-start'
          }}
        >
          <Typography 
            sx={{ 
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              color: '#667085'
            }}
          >
            Confirmed
          </Typography>
        </Box>

        {/* Restart Discovery (Location) */}
        <Button
          variant="text"
          size="small"
          onClick={async () => {
            try {
              await centralDiscoveryQueue.restartTopic('location');
              if (typeof window !== 'undefined' && typeof window.goToIntakeView === 'function') {
                window.goToIntakeView();
              }
            } catch (e) {
              console.error('Failed to restart discovery for location:', e);
            }
          }}
          sx={{
            textTransform: 'none',
            color: '#667085',
            alignSelf: 'flex-start',
            mt: 1,
            '&:hover': { backgroundColor: 'rgba(70, 83, 102, 0.04)' }
          }}
        >
          Restart discovery
        </Button>
      </Box>
    </Box>
  );
};

/**
 * LocationSelection component - interactive location selection with chip-based design matching Figma
 * @param {Object} props - Component props
 * @param {Object} props.locationMap - Map of locations to arrays of profiles
 * @param {Array} props.locationKeys - Array of location keys sorted by profile count
 * @param {Function} props.setMessages - Function to update messages
 * @param {Function} props.onSendText - Function to send text message
 * @param {Function} props.advanceToNextTopic - Function to advance to next topic
 * @param {Function} props.handleRefreshResults - Function to refresh match results
 * @param {Array} props.messages - Current conversation messages
 * @returns {JSX.Element} The rendered component
 */
export const LocationSelection = ({
  locationMap = {},
  locationKeys = [],
  setMessages,
  onSendText,
  advanceToNextTopic,
  handleRefreshResults,
  messages = []
}) => {
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  // Get profile count for a location
  const getProfileCount = (location) => {
    return Array.isArray(locationMap[location]) ? locationMap[location].length : 0;
  };

  // Calculate the total number of profiles across all locations
  const getTotalProfiles = () => {
    let total = 0;
    Object.keys(locationMap).forEach(location => {
      total += getProfileCount(location);
    });
    return total;
  };

  // Calculate percentage for a location
  const getLocationPercentage = (location) => {
    const count = getProfileCount(location);
    const total = getTotalProfiles();
    return total > 0 ? (count / total) * 100 : 0;
  };

  // Format the percentage for display
  const formatPercentage = (percentage) => {
    if (percentage < 1) {
      return "<1%";
    } else {
      return `${Math.round(percentage)}%`;
    }
  };

  // Handle location selection (chip behavior - multiple selection)
  const handleLocationToggle = (location) => {
    setSelectedLocations(prev => {
      if (prev.includes(location)) {
        // Remove if already selected
        return prev.filter(loc => loc !== location);
      } else {
        // Add if not selected, but first remove any remote options
        const withoutRemote = prev.filter(loc => !REMOTE_OPTIONS.some(option => option.label === loc));
        return [...withoutRemote, location];
      }
    });
  };

  // Handle remote option selection (exclusive with regular locations)
  const handleRemoteOptionToggle = (option) => {
    setSelectedLocations(prev => {
      if (prev.includes(option.label)) {
        // Remove if already selected
        return prev.filter(loc => loc !== option.label);
      } else {
        // Add remote option and remove all other selections (remote options are exclusive)
        return [option.label];
      }
    });
  };

  // Handle confirmation of selections
  const handleConfirmSelection = () => {
    if (selectedLocations.length === 0) return;
    
    setIsConfirmed(true);
    
    // Check if a remote option is selected
    const selectedRemoteOption = REMOTE_OPTIONS.find(option => 
      selectedLocations.includes(option.label)
    );
    
    let messageContent = '';
    
    if (selectedRemoteOption) {
      // Get the prompt string from the option and fix grammar if needed
      let promptStr = selectedRemoteOption.prompt_str;
      if (!promptStr.endsWith('.')) {
        promptStr += '.';
      }
      
      // Create message content for remote option
      messageContent = `${promptStr}`;
    } else {
      // Format the selected locations for display
      const locationText = selectedLocations.join(", ");
      
      // Create message content for regular locations
      if (selectedLocations.length === 1) {
        messageContent = `I want to target candidates in ${locationText}.`;
      } else {
        messageContent = `I want to target candidates in the following locations: ${locationText}.`;
      }
    }

    // Add the read-only location component to chat interface
    const userMessage = {
      role: 'User',
      content: messageContent,
      component: LocationSelectionReadOnly,
      componentProps: {
        selectedLocations: selectedLocations
      }
    };

    // Add AI summary message
    const aiSummaryMessage = {
      role: 'AI',
      content: `Perfect! I've noted your location preferences. Let's move on to the next topic to further refine your search criteria.`
    };

    // Update messages with both the user selection and AI summary
    setMessages(prev => [...prev, userMessage, aiSummaryMessage]);
    
    // Refresh results with the new messages
    if (handleRefreshResults) {
      const allMessages = [...messages, userMessage, aiSummaryMessage];
      handleRefreshResults(allMessages);
    }
    
    // Advance to next topic
    if (advanceToNextTopic) {
      setTimeout(() => {
        const updatedDiscoveryState = advanceToNextTopic();
        if (updatedDiscoveryState && updatedDiscoveryState.currentDiscoTopic && onSendText) {
          const moveOnMessage = `I am done with my selections for Location and am ready to move on to the next topic.`;
          setTimeout(() => {
            onSendText(moveOnMessage);
          }, 200);
        }
      }, 100);
    }
  };

  // Get top locations sorted by percentage
  const topLocations = locationKeys
    .sort((a, b) => getLocationPercentage(b) - getLocationPercentage(a))
    .slice(0, 4); // Show top 4 locations

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        borderRadius: '10px',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        marginBottom: '12px'
      }}
    >
      {/* Content */}
      <Box
        sx={{
          padding: '0 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <Typography 
          sx={{ 
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '1.375em',
            color: '#101828'
          }}
        >
          Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍
          {'\n'}Pick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨
        </Typography>

        {/* Row wrapper with border */}
        <Box
          sx={{
            border: '1px solid rgba(192, 197, 205, 0.5)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          {/* Common Locations Section */}
          <Box>
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '1.4285714285714286em',
                color: '#101828',
                marginBottom: '8px'
              }}
            >
              Common Locations
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {topLocations.map((location, index) => {
                const percentage = getLocationPercentage(location);
                const isSelected = selectedLocations.includes(location);
                const displayText = `${location} (${formatPercentage(percentage)})`;
                const textColor = isSelected ? '#0F42BC' : '#465366';
                
                return (
                  <Chip
                    key={index}
                    icon={
                      isSelected ? 
                        <CheckCircleIcon sx={{ fontSize: '14px !important', color: `${textColor} !important` }} /> :
                        <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important', color: `${textColor} !important` }} />
                    }
                    label={displayText}
                    onClick={() => handleLocationToggle(location)}
                    sx={{
                      backgroundColor: isSelected ? '#E1EFFF' : '#FFFFFF',
                      color: textColor,
                      border: isSelected ? 'none' : '1px solid #EAECF0',
                      fontFamily: 'Roboto',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '1.4285714285714286em',
                      borderRadius: '16px',
                      height: 'auto',
                      padding: '4px 12px 4px 10px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isSelected ? '#E1EFFF' : '#F9FAFB',
                      },
                      '& .MuiChip-label': {
                        padding: '0 4px 0 0'
                      },
                      '& .MuiChip-icon': {
                        margin: '0 4px 0 0',
                        color: `${textColor} !important`
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Remote Options Section */}
          <Box>
            <Typography 
              sx={{ 
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '1.4285714285714286em',
                color: '#101828',
                marginBottom: '8px'
              }}
            >
              or select a remote option
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {REMOTE_OPTIONS.map((option, index) => {
                const isSelected = selectedLocations.includes(option.label);
                const textColor = isSelected ? '#0F42BC' : '#465366';
                
                return (
                  <Chip
                    key={index}
                    icon={
                      isSelected ? 
                        <CheckCircleIcon sx={{ fontSize: '14px !important', color: `${textColor} !important` }} /> :
                        <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important', color: `${textColor} !important` }} />
                    }
                    label={option.label}
                    onClick={() => handleRemoteOptionToggle(option)}
                    sx={{
                      backgroundColor: isSelected ? '#E1EFFF' : '#FFFFFF',
                      color: textColor,
                      border: isSelected ? 'none' : '1px solid #EAECF0',
                      fontFamily: 'Roboto',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '1.4285714285714286em',
                      borderRadius: '16px',
                      height: 'auto',
                      padding: '4px 12px 4px 10px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isSelected ? '#E1EFFF' : '#F9FAFB',
                      },
                      '& .MuiChip-label': {
                        padding: '0 4px 0 0'
                      },
                      '& .MuiChip-icon': {
                        margin: '0 4px 0 0',
                        color: `${textColor} !important`
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Confirm Button */}
        <Button
          variant='contained'
          onClick={handleConfirmSelection}
          disabled={selectedLocations.length === 0 || isConfirmed}
          sx={{
            bgcolor: '#4599FA',
            color: '#FFFFFF',
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '1.4285714285714286em',
            textTransform: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            height: '36px',
            gap: '6px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'flex-start',
            '&:hover': { 
              bgcolor: '#3A7BD5' 
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(69, 153, 250, 0.3)',
              color: '#FFFFFF'
            }
          }}
        >
          {isConfirmed ? 'Location Confirmed' : 'Confirm Location'}
        </Button>
      </Box>
    </Box>
  );
};

// Legacy components for backward compatibility
export const LocationKeysChips = LocationSelection;
export const LocationOptions = LocationSelection;

export default LocationSelection;
